import { useMemo } from 'react'
import { IntlShape } from 'react-intl'

import { CustomerInfo } from 'models/customer/Customer'

import { ProgramCategory, SessionCountByProgram, SessionLimitByProgram } from '../../../models/session/SessionUsage'
import { ProgramCoverageBreakdown } from '../../../models/user/User'
import { convertDateStringFromISOToUSFormat } from '../../common/utils/dateUtils'
import {
  computeRemainderSessions,
  computeRemainderUsed,
  createRemainingSessionCountByProgram,
  isKnownProgramCategory,
  isProgramAvailableForMinor,
  isUnlimitedSessionLimit,
} from '../../common/utils/sessionLimit'
import { isMinor } from '../../searchForCare/utils'
import { programSupportForAttribute, resolveSessionLimit, shouldShowEAPSessionLimitInfo } from '../utils'
import { formatDualSessionsText } from '../utils/dualSessionsFormatter'

/**
 * Marks the "everything else" EAP row covering programs without an explicit
 * per-program session limit.
 */
export const EAP_SESSION_REMAINDER_CATEGORY = 'remainder'

type EAPSessionCountRow = {
  label: string
  value: string
  highlightValueAsWarning?: boolean
  programCategory: ProgramCategory | typeof EAP_SESSION_REMAINDER_CATEGORY
}

type ClientDetailsType = {
  date_of_birth: string
  employer: string
  sessions_limit_by_program?: SessionLimitByProgram
  sessions_used_by_program?: SessionCountByProgram
  cfp_sessions_limit?: number
  sessions_limit?: number
  sessions_used?: number
}

export const useGetEAPSessionCount = (
  forceAdultFlowsToHPI: boolean,
  intl: IntlShape,
  clientDetails?: ClientDetailsType | null,
  clientCustomer?: CustomerInfo,
  selectedEpisode?: { program_name?: string },
  programCoverageBreakdown?: ProgramCoverageBreakdown,
) => {
  return useMemo(() => {
    if (!clientDetails || !clientCustomer || !selectedEpisode) {
      return undefined
    }

    const isClientMinor = isMinor(convertDateStringFromISOToUSFormat(clientDetails.date_of_birth) ?? '')
    const allowedToShowEAPSessionLimitInfo = shouldShowEAPSessionLimitInfo(
      forceAdultFlowsToHPI,
      clientDetails?.employer,
      clientDetails.date_of_birth,
      selectedEpisode?.program_name,
    )
    if (!allowedToShowEAPSessionLimitInfo) {
      return []
    }

    const isCurrentProgramCoveredByEAP = programSupportForAttribute(
      'eapSupported',
      selectedEpisode,
      programCoverageBreakdown,
    )

    if (!isCurrentProgramCoveredByEAP) {
      return []
    }

    const sessionLimitsByProgram: SessionLimitByProgram | undefined = clientDetails.sessions_limit_by_program
    const sessionsLimit = resolveSessionLimit(selectedEpisode?.program_name, clientDetails, forceAdultFlowsToHPI)
    const sessionsUsed = clientDetails.sessions_used

    const perIssuePerYearLabel = intl.formatMessage({
      defaultMessage: 'Annual visits per issue (you can determine issues with your client)',
      description: 'Number of annual visits per issue per year',
    })

    if (clientCustomer?.display_visits_per_issue_per_year) {
      // Customer has specified display visits per issues
      // Applies to a minority of customers, examples: att union. more info here:
      // https://lyrahealth.atlassian.net/wiki/spaces/PROD/pages/2815590413/Visits+per+issue+per+year+for+union+negotiated+customers
      return [
        {
          label: perIssuePerYearLabel,
          value: clientCustomer.display_visits_per_issue_per_year.toString(),
        },
      ]
    } else if (clientCustomer?.display_visits_per_issue_per_year_by_program) {
      const dualSessionsText = formatDualSessionsText(
        clientCustomer.display_visits_per_issue_per_year_by_program,
        sessionLimitsByProgram,
      )

      if (dualSessionsText) {
        return [
          {
            label: perIssuePerYearLabel,
            value: dualSessionsText,
            customStyle: { marginRight: 30 },
          },
        ]
      }
    } else if (sessionLimitsByProgram) {
      // We have a count of sessions by the program, so show program specific counts
      const usedSessionsByProgram = clientDetails.sessions_used_by_program
      const remainingSessionsByProgram = createRemainingSessionCountByProgram(
        sessionLimitsByProgram,
        usedSessionsByProgram,
      )

      const perProgramRows: EAPSessionCountRow[] = Object.keys(remainingSessionsByProgram)
        .filter((category): category is ProgramCategory => isKnownProgramCategory(category))
        .filter((programCategory) => !isClientMinor || isProgramAvailableForMinor(programCategory))
        .map((programCategory) => {
          const sessionLimit = sessionLimitsByProgram[programCategory] ?? 0
          if (isUnlimitedSessionLimit(sessionLimit)) {
            return {
              label: intl.formatMessage(
                {
                  defaultMessage: 'EAP {programCategory} sessions used',
                  description: 'Label for unlimited sessions count',
                },
                { programCategory },
              ),
              value: intl.formatMessage({
                defaultMessage: 'Unlimited',
                description: 'Unlimited sessions count',
              }),
              programCategory,
            }
          } else {
            const usedSessions = usedSessionsByProgram?.[programCategory] ?? 0
            return {
              label: intl.formatMessage(
                {
                  defaultMessage: 'EAP {programCategory} sessions used',
                  description: 'Label for program sessions count',
                },
                { programCategory },
              ),
              value: intl.formatMessage(
                {
                  defaultMessage: '{numberOfUsedSessions} of {totalSessionsCount}',
                  description: 'Session count',
                },
                {
                  numberOfUsedSessions: usedSessions,
                  totalSessionsCount: sessionLimit,
                },
              ),
              highlightValueAsWarning: Boolean(usedSessions && usedSessions === sessionLimit),
              programCategory,
            }
          }
        })

      const remainder = computeRemainderSessions(sessionsLimit, sessionLimitsByProgram)
      if (remainder > 0) {
        const remainderUsed = computeRemainderUsed(sessionsUsed, sessionLimitsByProgram, usedSessionsByProgram)
        perProgramRows.push({
          label: intl.formatMessage({
            defaultMessage: 'EAP sessions used',
            description: 'Label for the remainder of EAP sessions not allocated to a specific program',
          }),
          value: intl.formatMessage(
            {
              defaultMessage: '{numberOfUsedSessions} of {totalSessionsCount}',
              description: 'Session count',
            },
            {
              numberOfUsedSessions: remainderUsed,
              totalSessionsCount: remainder,
            },
          ),
          highlightValueAsWarning: Boolean(remainderUsed && remainderUsed === remainder),
          programCategory: EAP_SESSION_REMAINDER_CATEGORY,
        })
      }

      return perProgramRows
    } else if (sessionsLimit !== undefined && sessionsUsed !== undefined) {
      // We don't have a count of sessions by program, so display sessions_limit and sessions_used
      return [
        {
          label: intl.formatMessage({
            defaultMessage: 'EAP sessions used',
            description: 'Number of EAP sessions used',
          }),
          value: intl.formatMessage(
            {
              defaultMessage: '{sessionsUsed} of {sessionsLimit}',
              description: 'EAP sessions used value',
            },
            { sessionsUsed, sessionsLimit },
          ),
          highlightValueAsWarning: Boolean(sessionsUsed && sessionsUsed === sessionsLimit),
        },
      ]
    }

    return []
  }, [forceAdultFlowsToHPI, intl, clientDetails, clientCustomer, selectedEpisode, programCoverageBreakdown])
}
